import React, { useState, useEffect } from 'react';

import FieldSetFormControl from '../component/FieldSetFormControl';
import LegendFormLabel from '../component/LegendFormLabel';
import SelectButton from '../component/SelectButton';
import ValidatedTextField from '../component/ValidatedTextField';

import Box from "@material-ui/core/Box";
import Divider from "@material-ui/core/Divider";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import FormGroup from "@material-ui/core/FormGroup";
import Radio from "@material-ui/core/Radio";
import RadioGroup from "@material-ui/core/RadioGroup";

import BN from 'bn.js';

const floatingPointFormats = {
  single: {
    totalWidth: 32,
    mantissaWidth: 23,
    exponentWidth: 8,
    exponentBias: 127
  },
  double: {
    totalWidth: 64,
    mantissaWidth: 52,
    exponentWidth: 11,
    exponentBias: 1023
  },
  quadruple: {
    totalWidth: 128,
    mantissaWidth: 112,
    exponentWidth: 15,
    exponentBias: 16383
  },
  octuple: {
    totalWidth: 256,
    mantissaWidth: 236,
    exponentWidth: 19,
    exponentBias: 262143
  }
};

function divRound (a, b) {
  return a.add (b.shrn (1)).div (b);
}

function toFloatingPoint (sign, numerator, denominator, format) {
  let mantissa, exponent;

  if (numerator.isZero ()) {
    mantissa = new BN (0);
    exponent = 0;
  } else {
    let shift = format.mantissaWidth + 2 - numerator.bitLength () + denominator.bitLength ();

    if (shift > 0) numerator = numerator.shln (shift);
    else if (shift < 0) numerator = numerator.shrn (-shift);

    mantissa = divRound (numerator, denominator);
    const delta = format.mantissaWidth + 1 - mantissa.bitLength ();

    if (delta > 0)
      mantissa = mantissa.shln (delta);
    else if (delta < 0)
      mantissa = mantissa.shrn (-delta);

    shift += delta;

    exponent = format.exponentBias + format.mantissaWidth - shift;
  }

  if (exponent > format.exponentBias * 2) {
    exponent = format.exponentBias * 2 + 1;
    mantissa = new BN (0);
  } else if (exponent < 1) {
    mantissa = mantissa.shrn (1 - exponent);
    exponent = 0;
  } else mantissa = mantissa.maskn (format.mantissaWidth);

  return '0x' + new BN (1).shln (format.totalWidth)
      .add (new BN (sign.isNeg () ? 1 : 0).shln (format.totalWidth - 1))
      .add (new BN (exponent).shln (format.mantissaWidth))
      .add (mantissa).toString (16).substring (1);
}

function ConvertNumber (props) {
  const {
    ...other
  } = props;

  const [ inputVariant, setInputVariant ] = useState ('plain');
  const [ input, setInput ] = useState ('');
  const [ plainFormat, setPlainFormat ] = useState ('dec');
  const [ plain, setPlain ] = useState ('');
  const [ wad, setWad ] = useState ('');
  const [ wadFormat, setWadFormat ] = useState ('dec');
  const [ ray, setRay ] = useState ('');
  const [ rayFormat, setRayFormat ] = useState ('dec');
  const [ fixed64x64, setFixed64x64 ] = useState ('');
  const [ fixed64x64Format, setFixed64x64Format ] = useState ('hex');
  const [ fixed128x128, setFixed128x128 ] = useState ('');
  const [ fixed128x128Format, setFixed128x128Format ] = useState ('hex');
  const [ single, setSingle ] = useState ('');
  const [ double, setDouble ] = useState ('');
  const [ quadruple, setQuadruple ] = useState ('');
  const [ octuple, setOctuple ] = useState ('');
  const [ error, setError ] = useState (undefined);

  useEffect (() => {
    try {
      const trimmedInput = input.trim ();
      if (trimmedInput === '')
        throw new Error ('Enter valid input value, please');

      const inputLowerCase = trimmedInput.toLowerCase ();

      var sign, numerator, denominator, precision;

      if (inputVariant === 'plain') {
        if (inputLowerCase === 'nan') {
          sign = new BN (1);
          numerator = new BN (0);
          denominator = new BN (0);
        } else if (inputLowerCase === '-infinity') {
          sign = new BN (-1);
          numerator = new BN (1);
          denominator = new BN (0);
        } else if (inputLowerCase === '+infinity' || inputLowerCase === 'infinity') {
          sign = new BN (1);
          numerator = new BN (1);
          denominator = new BN (0);
        } else {
          const match =
            inputLowerCase.match (/^(?<signSymbol>[-+])?(?:(?<integer>[0-9]+)(?:\.(?<fractional>[0-9]+))?(?:e(?<exponentSign>[-+])?(?<exponentValue>[0-9]+))?|0x(?<hex>[0-9a-f]+)|0b(?<bin>[01]+))$/);
  
          if (!match)
            throw new Error ('Invalid input format');
  
          const {
            signSymbol = '+',
            integer,
            fractional = '',
            exponentSign = '+',
            exponentValue = '0',
            hex,
            bin
          } = match.groups;
  
          sign = signSymbol === '+' ? new BN (1) : new BN (-1);
  
          if (integer) {
            numerator = new BN (integer + fractional);
            precision = numerator.toString ().length;
            denominator = new BN (10).pow (new BN (fractional.length));
            if (exponentSign === '+')
              numerator = numerator.mul (new BN (10).pow (new BN (exponentValue)));
            else
              denominator = denominator.mul (new BN (10).pow (new BN (exponentValue)));
          } else if (hex) {
            numerator = new BN (hex, 16);
            denominator = new BN (1);
            precision = numerator.toString ().length;
          } else if (bin) {
            numerator = new BN (bin, 2);
            denominator = new BN (1);
            precision = numerator.toString ().length;
          } else throw new Error ('Impossible');
        }
      } else if (inputVariant === 'wad' || inputVariant === 'ray') {
        const match =
          inputLowerCase.match (/^(?:(?<dec>[0-9]+)|0x(?<hex>[0-9a-f]+)|0b(?<bin>[01]+))$/);

        if (!match)
          throw new Error ('Invalid input format');

        const {
          dec,
          hex,
          bin
        } = match.groups;

        sign = new BN (1);
        denominator = new BN (10).pow (new BN (inputVariant === 'wad' ? 18 : 27));

        if (dec)
          numerator = new BN (dec);
        else if (hex)
          numerator = new BN (hex, 16);
        else if (bin)
          numerator = new BN (bin, 2);
        else throw new Error ('Impossible');

        if (numerator.bitLength () > 256)
          throw new Error ('Value too big');

        precision = numerator.toString ().length;
      } else if (inputVariant === '64.64' || inputVariant === '128.128') {
        const match =
          inputLowerCase.match (/^(?<signSymbol>[-+])?(?:(?<dec>[0-9]+)|0x(?<hex>[0-9a-f]+)|0b(?<bin>[01]+))$/);

        if (!match)
          throw new Error ('Invalid input format');

        const {
          signSymbol = '+',
          dec,
          hex,
          bin
        } = match.groups;

        sign = signSymbol === '+' ? new BN (1) : new BN (-1);
        denominator = new BN (1).shln (inputVariant === '64.64' ? 64 : 128);

        if (dec)
          numerator = new BN (dec);
        else if (hex)
          numerator = new BN (hex, 16);
        else if (bin)
          numerator = new BN (bin, 2);
        else throw new Error ('Impossible');

        if (numerator.sub (new BN (signSymbol === '+' ? 0 : 1)).bitLength () > (inputVariant === '64.64' ? 127 : 255))
          throw new Error ('Value too big');

        precision = numerator.toString ().length + 1;
      } else if (inputVariant === 'single' || inputVariant === 'double' || inputVariant === 'quadruple' || inputVariant === 'octuple') {
        const match =
          inputLowerCase.match (/^0x(?<hex>(?:[0-9a-f]{2})*)$/);

        if (!match)
          throw new Error ('Invalid input format');

        const {
          hex
        } = match.groups;

        const format = floatingPointFormats[inputVariant];

        if (hex.length * 4 !== format.totalWidth)
          throw new Error ('Invalid input length');

        const bn = new BN (hex, 16);

        sign = bn.testn (format.totalWidth - 1) ? new BN (-1) : new BN (1);
        const exponent = bn.shrn (format.mantissaWidth).maskn (format.exponentWidth);
        const mantissa = bn.maskn (format.mantissaWidth);

        if (exponent.isZero ()) {
          // Subnormal
          numerator = mantissa;
          precision = numerator.toString ().length + 1;
          denominator = new BN (1).shln (format.exponentBias - 1 + format.mantissaWidth);
        } else if (exponent.eq (new BN (format.exponentBias * 2 + 1))) {
          if (mantissa.isZero ()) {
            // Infinity
            numerator = new BN (1);
          } else {
            // NaN
            numerator = new BN (0);
          }
          denominator = new BN (0);
          precision = 0;
        } else {
          numerator = mantissa.add (new BN (1).shln (format.mantissaWidth));
          precision = numerator.toString ().length + 1;
          denominator = new BN (1).shln (format.mantissaWidth);

          if (exponent.gt (new BN (format.exponentBias)))
            numerator = numerator.shln (exponent.sub (new BN (format.exponentBias)).toNumber ());
          else
            denominator = denominator.shln (new BN (format.exponentBias).sub (exponent).toNumber ());
        }
      } else throw new Error ('Impossible');

      if (!denominator.isZero ()) {
        const gcd = numerator.gcd (denominator);
        numerator = numerator.div (gcd);
        denominator = denominator.div (gcd);
      }

      if (denominator.isZero ()) {
        if (numerator.isZero ()) {
          setPlain ('NaN');
          setWad ('');
          setRay ('');
          setFixed64x64 ('');
          setFixed128x128 ('');
          setSingle ('0x7f800001');
          setDouble ('0x7ff0000000000001');
          setQuadruple ('0x7fff0000000000000000000000000001');
          setOctuple ('0x7ffff00000000000000000000000000000000000000000000000000000000001');
        } else if (sign.isNeg ()) {
          setPlain ('-Infinity');
          setWad ('');
          setRay ('');
          setFixed64x64 ('');
          setFixed128x128 ('');
          setSingle ('0xff800000');
          setDouble ('0xfff0000000000000');
          setQuadruple ('0xffff0000000000000000000000000000');
          setOctuple ('0xfffff00000000000000000000000000000000000000000000000000000000000');
        } else {
          setPlain ('Infinity');
          setWad ('');
          setRay ('');
          setFixed64x64 ('');
          setFixed128x128 ('');
          setSingle ('0x7f800000');
          setDouble ('0x7ff0000000000000');
          setQuadruple ('0x7fff0000000000000000000000000000');
          setOctuple ('0x7ffff00000000000000000000000000000000000000000000000000000000000');
        }
      } else {
        const signSymbol = sign.isNeg () ? '-' : '';

        if (plainFormat === 'dec') {
          const shift = precision - numerator.toString ().length + denominator.toString ().length;
          let value = divRound (numerator.mul (new BN (10).pow (new BN (Math.max (0, shift)))), denominator).toString ();
          if (shift > 0) {
            while (value.length < shift + 1)
              value = '0' + value;
  
            value = value.substring (0, value.length - shift) + '.' + value.substring (value.length - shift);
  
            value = value.replace (/(?:\.0*|(\.[0-9]*[1-9])0+)$/, '$1');
          }
          setPlain (signSymbol + value);
        } else if (plainFormat === 'hex') {
          if (denominator.eq (new BN (1)))
            setPlain (signSymbol + '0x' + numerator.toString (16));
          else setPlain ('');
        } else if (plainFormat === 'bin') {
          if (denominator.eq (new BN (1)))
            setPlain (signSymbol + '0b' + numerator.toString (2));
          else setPlain ('');
        } else if (plainFormat === 'sci') {
          let shift = Math.max (precision, 2) - numerator.toString ().length + denominator.toString ().length;
          shift = Math.max (0, shift);
          let value = divRound (numerator.mul (new BN (10).pow (new BN (shift))), denominator).toString ();
          shift = value === '0' ? 0 : shift - value.length + 1;
          value = value.substring (0, 1) + '.' + value.substring (1);
          if (value.endsWith ('.')) value = value + '0';
          value = value.replace (/(\.[0-9]*[1-9])0+$/, '$1');
          value = value + 'e' + (-shift);
          setPlain (signSymbol + value);
        } else
          throw new Error ('Impossible');

        const wadValue = divRound (numerator.mul (new BN (10).pow (new BN (18))), denominator);
        if ((!sign.isNeg () || wadValue.isZero ()) && wadValue.lt (new BN (1).shln (256))) {
          if (wadFormat === 'dec')
            setWad (wadValue.toString ());
          else if (wadFormat === 'hex')
            setWad ('0x' + wadValue.toString (16));
          else if (wadFormat === 'bin')
            setWad ('0b' + wadValue.toString (2));
          else
            throw new Error ('Impossible');
        } else setWad ('');

        const rayValue = divRound (numerator.mul (new BN (10).pow (new BN (27))), denominator);
        if ((!sign.isNeg () || rayValue.isZero ()) && rayValue.lt (new BN (1).shln (256))) {
          if (rayFormat === 'dec')
            setRay (rayValue.toString ());
          else if (rayFormat === 'hex')
            setRay ('0x' + rayValue.toString (16));
          else if (rayFormat === 'bin')
            setRay ('0b' + rayValue.toString (2));
          else
            throw new Error ('Impossible');
        } else setRay ('');

        const fixed64x64Value = divRound (numerator.shln (64), denominator);
        if (fixed64x64Value.sub (new BN (sign.isNeg () ? 1 : 0)).lt (new BN (1).shln (127))) {
          if (fixed64x64Format === 'dec')
            setFixed64x64 (fixed64x64Value.toString ());
          else if (fixed64x64Format === 'hex')
            setFixed64x64 ('0x' + fixed64x64Value.toString (16));
          else if (fixed64x64Format === 'bin')
            setFixed64x64 ('0b' + fixed64x64Value.toString (2));
          else
            throw new Error ('Impossible');
        } else setFixed64x64 ('');

        const fixed128x128Value = divRound (numerator.shln (128), denominator);
        if (fixed128x128Value.sub (new BN (sign.isNeg () ? 1 : 0)).lt (new BN (1).shln (255))) {
          if (fixed128x128Format === 'dec')
            setFixed128x128 (fixed128x128Value.toString ());
          else if (fixed128x128Format === 'hex')
            setFixed128x128 ('0x' + fixed128x128Value.toString (16));
          else if (fixed128x128Format === 'bin')
            setFixed128x128 ('0b' + fixed128x128Value.toString (2));
          else
            throw new Error ('Impossible');
        } else setFixed128x128 ('');
 
        setSingle (toFloatingPoint (sign, numerator, denominator, floatingPointFormats.single));
        setDouble (toFloatingPoint (sign, numerator, denominator, floatingPointFormats.double));
        setQuadruple (toFloatingPoint (sign, numerator, denominator, floatingPointFormats.quadruple));
        setOctuple (toFloatingPoint (sign, numerator, denominator, floatingPointFormats.octuple));
      }

      // console.log (sign + '*' + numerator + '/' + denominator + " (" + precision + ")");

      setError (undefined);
    } catch (error) {
      // console.log (error);
      setPlain ('');
      setWad ('');
      setRay ('');
      setFixed64x64 ('');
      setFixed128x128 ('');
      setSingle ('');
      setDouble ('');
      setQuadruple ('');
      setOctuple ('');
      setError (error.message);
    }
  }, [ inputVariant, input, plainFormat, wadFormat, rayFormat, fixed64x64Format, fixed128x128Format ]);

  return (
    <Box my={-1}
         component={FieldSetFormControl}
         fullWidth
         {...other}>
      <Box component={RadioGroup}
           row
           value={inputVariant}
           onChange={event => setInputVariant (event.target.value)}>
        <Box alignSelf="center"
             mr={2}
             component={LegendFormLabel}>
          Input format:
        </Box>
        <FormControlLabel label="Plain"
                          control={<Radio />}
                          value="plain" />
        <FormControlLabel label="Wad"
                          control={<Radio />}
                          value="wad" />
        <FormControlLabel label="Ray"
                          control={<Radio />}
                          value="ray" />
        <FormControlLabel label="64.64"
                          control={<Radio />}
                          value="64.64" />
        <FormControlLabel label="128.128"
                          control={<Radio />}
                          value="128.128" />
        <FormControlLabel label="Single"
                          control={<Radio />}
                          value="single" />
        <FormControlLabel label="Double"
                          control={<Radio />}
                          value="double" />
        <FormControlLabel label="Quadruple"
                          control={<Radio />}
                          value="quadruple" />
        <FormControlLabel label="Octuple"
                          control={<Radio />}
                          value="octuple" />
      </Box>
      <Box my={1}
           component={ValidatedTextField}
           label="Input"
           placeholder={
             inputVariant === 'plain' ? '+0.0e+0' :
             inputVariant === 'wad' || inputVariant === 'ray' ? '0' :
             inputVariant === '64.64' ? '+0' :
             inputVariant === '128.128' ? '+0' :
             inputVariant === 'single' ? '0x00000000' :
             inputVariant === 'double' ? '0x0000000000000000' :
             inputVariant === 'quadruple' ? '0x00000000000000000000000000000000' :
             inputVariant === 'octuple' ? '0x0000000000000000000000000000000000000000000000000000000000000000' :
             undefined
           }
           value={input}
           validator={() => error}
           onChange={event => setInput (event.target.value)} />
      <Box mt={1}
           mb={3}>
        <Divider />
      </Box>
      <Box mx={-1}
           component={FormGroup}
           row>
        <Box m={1}
             flexGrow={1}
             component={ValidatedTextField}
             label="Plain"
             readOnly
             value={plain}
             startAdornment={
               <SelectButton value={plainFormat}
                             options={[
                               { value: 'dec', title: 'Dec' },
                               { value: 'hex', title: 'Hex' },
                               { value: 'bin', title: 'Bin' },
                               { value: 'sci', title: 'Sci' }
                             ]}
                             onChange={setPlainFormat} />
             } />
        <Box m={1}
             flexGrow={1}
             component={ValidatedTextField}
             label="Wad"
             readOnly
             value={wad}
             startAdornment={
               <SelectButton value={wadFormat}
                             options={[
                               { value: 'dec', title: 'Dec' },
                               { value: 'hex', title: 'Hex' },
                               { value: 'bin', title: 'Bin' }
                             ]}
                             onChange={setWadFormat} />
             } />
        <Box m={1}
             flexGrow={1}
             component={ValidatedTextField}
             label="Ray"
             readOnly
             value={ray}
             startAdornment={
               <SelectButton value={rayFormat}
                             options={[
                               { value: 'dec', title: 'Dec' },
                               { value: 'hex', title: 'Hex' },
                               { value: 'bin', title: 'Bin' }
                             ]}
                             onChange={setRayFormat} />
             } />
        <Box m={1}
             flexGrow={1}
             component={ValidatedTextField}
             label="64.64"
             readOnly
             value={fixed64x64}
             startAdornment={
               <SelectButton value={fixed64x64Format}
                             options={[
                               { value: 'dec', title: 'Dec' },
                               { value: 'hex', title: 'Hex' },
                               { value: 'bin', title: 'Bin' }
                             ]}
                             onChange={setFixed64x64Format} />
             } />
        <Box m={1}
             flexGrow={1}
             component={ValidatedTextField}
             label="128.128"
             readOnly
             value={fixed128x128}
             startAdornment={
               <SelectButton value={fixed128x128Format}
                             options={[
                               { value: 'dec', title: 'Dec' },
                               { value: 'hex', title: 'Hex' },
                               { value: 'bin', title: 'Bin' }
                             ]}
                             onChange={setFixed128x128Format} />
             } />
        <Box m={1}
             flexGrow={1}
             component={ValidatedTextField}
             label="Single"
             readOnly
             value={single} />
        <Box m={1}
             flexGrow={1}
             component={ValidatedTextField}
             label="Double"
             readOnly
             value={double} />
        <Box m={1}
             flexGrow={1}
             component={ValidatedTextField}
             label="Quadruple"
             readOnly
             value={quadruple} />
        <Box m={1}
             flexGrow={1}
             component={ValidatedTextField}
             label="Octuple"
             readOnly
             value={octuple} />
      </Box>
    </Box>
  );
}

export default ConvertNumber;
